import {
  collection,
  DocumentData,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
  WhereFilterOp,
} from 'firebase/firestore';

import { firestore } from '../Firebase';

export interface FilterItem {
  operator: WhereFilterOp;
  value: Date | string | number | boolean | Timestamp | string[];
  attribute: string;
}

export interface OrderItem {
  type: 'asc' | 'desc';
  field: string;
}

export const getQuery = (
  collectionName: string,
  filters?: FilterItem[],
  ordering?: OrderItem[],
  last?: DocumentData,
  limitAmount?: number,
) => {
  filters = filters ?? [];
  ordering = ordering ?? [];

  const collectionRef = collection(firestore, collectionName);
  const queryRef = query(
    collectionRef,
    ...filters.map((filter) => where(filter?.attribute, filter?.operator, filter?.value)),
    ...ordering.map((order) => orderBy(order.field, order.type)),
    ...(last ? [startAfter(last)] : []),
    ...(limitAmount ? [limit(limitAmount)] : []),
  );

  return queryRef;
};

export const getDocuments = async (
  collectionName: string,
  filters?: FilterItem[],
  ordering?: OrderItem[],
  last?: DocumentData,
  limitAmount?: number,
) => {
  const queryRef = getQuery(collectionName, filters, ordering, last, limitAmount);

  return await getDocs(queryRef);
};

export const getRevenueQuery = (
  filters: FilterItem[],
  ordering: OrderItem[],
  last: DocumentData | undefined,
  limitAmount: number | undefined = undefined,
) => getQuery('insights', filters, ordering, last, limitAmount);

export const getRevenue = (
  filters: FilterItem[],
  ordering: OrderItem[],
  last: DocumentData | undefined,
  limitAmount: number | undefined = undefined,
) => getDocuments('insights', filters, ordering, last, limitAmount);

export const getRevenueRecordsQuery = (
  insightId: string,
  filters: FilterItem[],
  ordering: OrderItem[],
  last: DocumentData | undefined,
  limitAmount: number | undefined = undefined,
) => getQuery(`insights/${insightId}/records`, filters, ordering, last, limitAmount);

export const getRevenueRecords = (
  insightId: string,
  filters: FilterItem[],
  ordering: OrderItem[],
  last: DocumentData | undefined,
  limitAmount: number | undefined = undefined,
) => getDocuments(`insights/${insightId}/records`, filters, ordering, last, limitAmount);

export const getArtistRevenueQuery = (
  artistId: string,
  filters: FilterItem[],
  ordering: OrderItem[],
  last: DocumentData | undefined,
  limitAmount: number | undefined = undefined,
) => getQuery(`usersWeb/${artistId}/insights`, filters, ordering, last, limitAmount);

export const getArtistRevenue = (
  artistId: string,
  filters: FilterItem[],
  ordering: OrderItem[],
  last: DocumentData | undefined,
  limitAmount: number | undefined = undefined,
) => getDocuments(`usersWeb/${artistId}/insights`, filters, ordering, last, limitAmount);
